import { useEffect, useMemo, useRef, useState } from "react";
import { ReactFlow, Background, BackgroundVariant, Controls } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Save, Download, Share2, FolderOpen, Plus, Trash2, Loader2, X, GitFork } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useSdkStore, NODE_CATALOG, MOODS } from "./sdkStore";
import { nodeTypes } from "./nodes";

const inputCls = "w-full bg-[#050505] border border-white/10 px-2.5 py-2 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue";
const labelCls = "font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase block mb-1";

function Slider({ label, value, min, max, step = 1, onChange, suffix = "", testid }) {
  return (
    <div>
      <div className="flex justify-between items-baseline mb-1">
        <span className={labelCls}>{label}</span>
        <span className="font-mono text-[10px] text-nc-orange">{value}{suffix}</span>
      </div>
      <input data-testid={testid} type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full accent-[#FF6B1A]" />
    </div>
  );
}

function Inspector() {
  const { nodes, selectedId, updateNodeData } = useSdkStore();
  const node = nodes.find((n) => n.id === selectedId);
  if (!node) {
    return (
      <div data-testid="inspector-empty" className="font-mono text-[10px] text-zinc-600 leading-relaxed">
        Select a node to configure it. Click palette items to add nodes, drag from a node's bottom port to connect. Delete key removes the selection.
      </div>
    );
  }
  const d = node.data;
  const set = (k, v) => updateNodeData(node.id, { [k]: v });
  return (
    <div data-testid="inspector" className="flex flex-col gap-3">
      <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-nc-blue">{node.type} // {d.kind}</p>
      <div>
        <span className={labelCls}>Label</span>
        <input data-testid="inspector-label" className={inputCls} value={d.label || ""} maxLength={48} onChange={(e) => set("label", e.target.value)} />
      </div>
      {d.kind === "area_arrival" && <div><span className={labelCls}>Zone / sector</span><input className={inputCls} value={d.zone || ""} onChange={(e) => set("zone", e.target.value)} /></div>}
      {d.kind === "shots_fired" && <Slider label="Rounds threshold" testid="inspector-threshold" value={d.threshold ?? 1} min={1} max={20} onChange={(v) => set("threshold", v)} />}
      {d.kind === "call_911" && (
        <div><span className={labelCls}>Call priority</span>
          <select className={inputCls} value={d.priority || "Priority 1"} onChange={(e) => set("priority", e.target.value)}>
            {["Priority 1", "Priority 2", "Priority 3"].map((p) => <option key={p}>{p}</option>)}
          </select>
        </div>
      )}
      {d.kind === "time_elapsed" && <Slider label="Seconds after start" testid="inspector-seconds" value={d.seconds ?? 60} min={5} max={600} step={5} onChange={(v) => set("seconds", v)} suffix="s" />}
      {d.kind === "escalation_chance" && <Slider label="Escalation chance" testid="inspector-chance" value={d.chance ?? 50} min={0} max={100} onChange={(v) => set("chance", v)} suffix="%" />}
      {d.kind === "mood_check" && (
        <div><span className={labelCls}>Branch if mood is</span>
          <select className={inputCls} value={d.mood || "hostile"} onChange={(e) => set("mood", e.target.value)}>{MOODS.map((m) => <option key={m}>{m}</option>)}</select>
        </div>
      )}
      {d.kind === "sightline_check" && <Slider label="Sightline range" testid="inspector-range" value={d.range ?? 30} min={5} max={150} onChange={(v) => set("range", v)} suffix="m" />}
      {node.type === "profile" && (
        <>
          <div><span className={labelCls}>Mood threshold</span>
            <select data-testid="inspector-mood" className={inputCls} value={d.mood || "agitated"} onChange={(e) => set("mood", e.target.value)}>{MOODS.map((m) => <option key={m}>{m}</option>)}</select>
          </div>
          <Slider label="Compliance rate" testid="inspector-compliance" value={d.compliance ?? 50} min={0} max={100} onChange={(v) => set("compliance", v)} suffix="%" />
          <Slider label="Negotiation resistance" testid="inspector-resistance" value={d.negotiation_resistance ?? 1} min={0.2} max={3} step={0.1} onChange={(v) => set("negotiation_resistance", v)} suffix="x" />
        </>
      )}
      {d.kind === "spawn_swat" && (
        <>
          <Slider label="SWAT units" testid="inspector-units" value={d.units ?? 4} min={1} max={12} onChange={(v) => set("units", v)} />
          <div><span className={labelCls}>Staging zone</span><input className={inputCls} value={d.staging || ""} onChange={(e) => set("staging", e.target.value)} /></div>
        </>
      )}
      {d.kind === "trigger_barricade" && <div><span className={labelCls}>Barricade location</span><input className={inputCls} value={d.location || ""} onChange={(e) => set("location", e.target.value)} /></div>}
      {d.kind === "radio_broadcast" && <div><span className={labelCls}>Broadcast message</span><textarea rows={3} className={`${inputCls} resize-none`} value={d.message || ""} maxLength={200} onChange={(e) => set("message", e.target.value)} /></div>}
      {d.kind === "set_hostage_state" && (
        <div><span className={labelCls}>Hostage state</span>
          <select className={inputCls} value={d.state || "captive"} onChange={(e) => set("state", e.target.value)}>
            {["captive", "negotiating", "released", "harmed", "rescued"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      )}
    </div>
  );
}

export default function BlueprintEditor({ me }) {
  const { nodes, edges, mission, setMissionMeta, onNodesChange, onEdgesChange, onConnect, addNode, loadMission, resetMission, graphPayload } = useSdkStore();
  const wrapRef = useRef(null);
  const [busy, setBusy] = useState("");
  const [mine, setMine] = useState(null);

  const loggedIn = !!me;

  const save = async () => {
    if (!loggedIn) { toast.error("Sign in to save blueprints"); return; }
    if (mission.name.trim().length < 3) { toast.error("Give the blueprint a name (3+ chars)"); return; }
    setBusy("save");
    try {
      const payload = { name: mission.name, description: mission.description, scenario: mission.scenario, service_tag: mission.service_tag, difficulty: mission.difficulty, shared: mission.shared, graph: graphPayload() };
      if (mission.id) {
        await api.put(`/sdk/missions/${mission.id}`, payload);
        toast.success("Blueprint saved");
      } else {
        const r = await api.post("/sdk/missions", payload);
        loadMission(r.data);
        toast.success(mission.shared ? "Published to the Workshop" : "Blueprint saved");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(""); }
  };

  const exportFile = (fmt) => {
    if (!mission.id) { toast.error("Save the blueprint first — exports compile server-side"); return; }
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sdk/missions/${mission.id}/export.${fmt}`, "_blank");
  };

  const toggleMine = async () => {
    if (mine) { setMine(null); return; }
    if (!loggedIn) { toast.error("Sign in to open your blueprints"); return; }
    try {
      const r = await api.get("/sdk/missions/mine");
      setMine(r.data.missions);
    } catch { toast.error("Could not load your blueprints"); }
  };

  return (
    <div data-testid="blueprint-editor" className="flex flex-col gap-4">
      {/* toolbar */}
      <div className="border border-white/10 bg-[#070707] p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-52">
          <span className={labelCls}>Blueprint name</span>
          <input data-testid="mission-name" className={inputCls} value={mission.name} maxLength={80} onChange={(e) => setMissionMeta({ name: e.target.value })} />
        </div>
        <div>
          <span className={labelCls}>Scenario</span>
          <select data-testid="mission-scenario" className={inputCls} value={mission.scenario} onChange={(e) => setMissionMeta({ scenario: e.target.value })}>
            <option value="active_shooter">Active Shooter</option>
            <option value="barricade">Barricade / Hostage</option>
            <option value="freeform">Freeform</option>
          </select>
        </div>
        <div>
          <span className={labelCls}>Service</span>
          <select className={inputCls} value={mission.service_tag} onChange={(e) => setMissionMeta({ service_tag: e.target.value })}>
            <option value="police">Police</option><option value="fire">Fire</option><option value="ems">EMS</option>
          </select>
        </div>
        <div>
          <span className={labelCls}>Difficulty</span>
          <select className={inputCls} value={mission.difficulty} onChange={(e) => setMissionMeta({ difficulty: e.target.value })}>
            {["Recruit", "Moderate", "Veteran", "Elite"].map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <label className="flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.15em] text-zinc-400 cursor-pointer pb-2.5">
          <input data-testid="mission-shared" type="checkbox" checked={mission.shared} onChange={(e) => setMissionMeta({ shared: e.target.checked })} className="accent-[#FF6B1A]" />
          Share in Workshop
        </label>
        <div className="flex gap-2">
          <button data-testid="blueprint-new" onClick={() => { resetMission(); setMine(null); }} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-white/15 text-zinc-400 hover:text-zinc-100 px-3 py-2.5 transition-colors flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New</button>
          <button data-testid="blueprint-open" onClick={toggleMine} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-white/15 text-zinc-400 hover:text-zinc-100 px-3 py-2.5 transition-colors flex items-center gap-1.5"><FolderOpen className="w-3.5 h-3.5" /> Mine</button>
          <button data-testid="blueprint-save" disabled={busy === "save"} onClick={save} className="font-mono text-[9px] tracking-[0.15em] uppercase bg-nc-blue text-white hover:bg-nc-blue/80 px-4 py-2.5 transition-colors flex items-center gap-1.5 disabled:opacity-50">
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {mission.id ? "Save" : "Save New"}
          </button>
          <button data-testid="export-json" onClick={() => exportFile("json")} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-3 py-2.5 transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> .json</button>
          <button data-testid="export-lua" onClick={() => exportFile("lua")} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-3 py-2.5 transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> .lua</button>
        </div>
      </div>

      {mission.ancestor_id && (
        <p className="font-mono text-[9px] text-zinc-500 flex items-center gap-1.5"><GitFork className="w-3 h-3 text-nc-orange" /> Remixed from blueprint {mission.ancestor_id.slice(0, 8)} — your edits stay yours.</p>
      )}

      {mine && (
        <div data-testid="mine-list" className="border border-white/10 bg-[#070707] p-3 flex flex-col gap-1.5 max-h-48 overflow-y-auto">
          {mine.length === 0 && <p className="font-mono text-[10px] text-zinc-600 p-2">No saved blueprints yet.</p>}
          {mine.map((m) => (
            <button key={m.id} data-testid={`mine-open-${m.id.slice(0, 8)}`} onClick={() => { loadMission(m); setMine(null); toast.success(`Loaded "${m.name}"`); }} className="flex items-center justify-between text-left border border-white/5 hover:border-nc-blue/40 px-3 py-2 transition-colors">
              <span className="font-mono text-[10px] text-zinc-200">{m.name}</span>
              <span className="font-mono text-[8px] text-zinc-500 uppercase">{m.node_count} nodes {m.shared ? "· shared" : ""}</span>
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_260px] gap-4">
        {/* palette */}
        <div data-testid="node-palette" className="border border-white/10 bg-[#070707] p-3 flex flex-col gap-4 max-h-[640px] overflow-y-auto">
          {NODE_CATALOG.map((cat) => (
            <div key={cat.type}>
              <p className="font-mono text-[8px] tracking-[0.3em] uppercase mb-2" style={{ color: cat.color }}>{cat.label}</p>
              <div className="flex flex-col gap-1.5">
                {cat.kinds.map((k) => (
                  <button
                    key={k.kind}
                    data-testid={`palette-${k.kind}`}
                    onClick={() => addNode(cat.type, k.kind, { x: 120 + Math.random() * 240, y: 80 + Math.random() * 220 })}
                    className="text-left font-mono text-[10px] text-zinc-300 border border-white/10 hover:border-white/40 bg-black/40 px-3 py-2 transition-colors flex items-center gap-2"
                  >
                    <Plus className="w-3 h-3 shrink-0" style={{ color: cat.color }} /> {k.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* canvas */}
        <div ref={wrapRef} data-testid="blueprint-canvas" className="border border-white/10 bg-black h-[640px]">
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={nodeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
            fitView colorMode="dark" deleteKeyCode={["Backspace", "Delete"]} proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={22} size={1} color="#27272a" />
            <Controls showInteractive={false} />
          </ReactFlow>
        </div>

        {/* inspector */}
        <div className="border border-white/10 bg-[#070707] p-4 max-h-[640px] overflow-y-auto">
          <Inspector />
        </div>
      </div>
    </div>
  );
}
